import { cn } from "@/lib/utils";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../ui/button";

import { useProjectWithUsers } from "@/hooks/useProjectWithUsers";
import { SavedView, useViews } from "@/components/views/hooks/useViews";

export interface ViewsNavProps {
  onViewSelect: (viewId: string) => void;
  selectedViewId?: string;
}

export default function ViewsNav({
  onViewSelect,
  selectedViewId,
}: ViewsNavProps) {
  const currentProject = useProjectWithUsers();
  const { getViews, deleteView } = useViews();

  const projectViews = getViews(currentProject?.id || "");

  const handleViewSelect = (view: SavedView) => {
    onViewSelect(view.id);
  };

  const handleDeleteView = async (view: SavedView) => {
    if (!view.isDefault && currentProject?.id) {
      if (selectedViewId === view.id) {
        const remainingViews = projectViews.filter((v) => v.id !== view.id);
        if (remainingViews.length > 0) {
          const firstRemainingView = remainingViews[0];
          onViewSelect(firstRemainingView.id);
        } else {
          onViewSelect("create-new");
        }
      }

      await deleteView(currentProject.id, view.id);
    }
  };

  if (projectViews.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center pl-4 pr-2 py-2 justify-between">
        <h2 className="flex flex-col text-xs font-normal">Views</h2>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => onViewSelect("create-new")}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-col gap-1 p-2">
        {projectViews.map((view) => (
          <div
            key={view.id}
            className={cn(
              "flex items-center p-1 rounded-md h-6 transition-colors group",
              "hover:bg-secondary-n-950",
              selectedViewId === view.id && "bg-secondary-n-900",
            )}
          >
            <div className="grow">
              <div
                onClick={() => handleViewSelect(view)}
                className="text-xs pl-1 font-normal w-full text-left cursor-pointer"
              >
                {view.name}
              </div>
            </div>

            {!view.isDefault && (
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  className="rounded-sm p-0.5 min-w-5 flex items-center justify-center hover:text-red-400"
                  onClick={() => handleDeleteView(view)}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
